import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  getTableDetail,
  type TableDetailResult,
} from '../../services/data-integration-api';

interface TableDetailProps {
  tableCatalogId: string;
  onBack?: () => void;
  onCreateMapping?: (tableCatalogId: string) => void;
}

export default function TableDetail({ tableCatalogId, onBack, onCreateMapping }: TableDetailProps) {
  const [data, setData] = useState<TableDetailResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const result = await getTableDetail(tableCatalogId);
        setData(result);
      } catch (e: any) {
        setError(e.message || '加载失败');
      } finally {
        setLoading(false);
      }
    })();
  }, [tableCatalogId]);

  if (loading) return <div className="p-6 text-muted-foreground">加载中...</div>;
  if (error) return <div className="p-6 text-destructive">错误: {error}</div>;
  if (!data) return <div className="p-6 text-muted-foreground">无数据</div>;

  const { table, columns, foreignKeys } = data as any;

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <h2 className="font-mono font-semibold text-lg">{table.table_name}</h2>
          {table.table_comment && <span className="text-muted-foreground">{table.table_comment}</span>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">估计行数: {table.row_count_estimate?.toLocaleString()}</span>
          <span className="text-sm text-muted-foreground">版本: v{table.catalog_version}</span>
          {onCreateMapping && (
            <Button onClick={() => onCreateMapping(tableCatalogId)}>创建规范存取表</Button>
          )}
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-semibold mb-2">字段列表 ({columns.length})</h3>
        <Table className="mb-6">
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>字段名</TableHead>
              <TableHead>类型</TableHead>
              <TableHead>长度</TableHead>
              <TableHead>可空</TableHead>
              <TableHead>默认值</TableHead>
              <TableHead>键</TableHead>
              <TableHead>注释</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {columns.map((col: any) => (
              <TableRow key={col.column_name}>
                <TableCell>{col.ordinal_position}</TableCell>
                <TableCell className={`font-mono ${col.is_primary_key ? 'font-bold' : ''}`}>
                  {col.column_name}
                </TableCell>
                <TableCell className="font-mono">{col.data_type}</TableCell>
                <TableCell>{col.character_max_length || col.numeric_precision || '-'}</TableCell>
                <TableCell>{col.is_nullable ? 'YES' : 'NO'}</TableCell>
                <TableCell className="font-mono text-muted-foreground">
                  {col.column_default ?? '-'}
                </TableCell>
                <TableCell>
                  {col.is_primary_key && <Badge className="bg-amber-100 text-amber-800">PK</Badge>}
                  {col.column_key_type === 'UNI' && <Badge className="bg-blue-100 text-blue-800">UNI</Badge>}
                  {col.column_key_type === 'MUL' && <Badge variant="secondary">MUL</Badge>}
                </TableCell>
                <TableCell className="text-muted-foreground">{col.column_comment || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {foreignKeys.length > 0 && (
          <>
            <h3 className="font-semibold mb-2">外键关系 ({foreignKeys.length})</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>约束名</TableHead>
                  <TableHead>本表字段</TableHead>
                  <TableHead>引用表</TableHead>
                  <TableHead>引用字段</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {foreignKeys.map((fk: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono">{fk.constraint_name}</TableCell>
                    <TableCell className="font-mono">{fk.source_column_name}</TableCell>
                    <TableCell className="font-mono">{fk.target_table_name}</TableCell>
                    <TableCell className="font-mono">{fk.target_column_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </div>
  );
}
